import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { apiFetch, apiFetchJSON } from "../api/apiClient";
import { usePageTour } from "../components/tour/tourStore";

import DesktopLayout from "./DesktopLayout";
import MobileLayout from "./MobileLayout";

function MainPage({ userType, user, onLogout }) {
  const { serialNumber } = useParams();

  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  // 'daily' | 'weekly' | 'monthly'
  const [period, setPeriod] = useState("daily");
  const [deviceData, setDeviceData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("Loading...");
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Peplink management type, from the SAME source Site Dashboard uses
  // (sites.service peplink.get_status -> peplinkManagementType). Decides
  // which live fetch / priority conversion is used, so it's never guessed:
  // null/'unknown' => priority change stays disabled.
  const [managementType, setManagementType] = useState(null);
  // false when the router is in a read-only InControl organisation
  // (no admin rights there), so WAN priority change is hidden.
  const [ic2Writable, setIc2Writable] = useState(false);
  // Starlink/ELCOME fallback position for the location map. Only fetched
  // when the Peplink report has no GPS trail. Single LIVE point, not history.
  const [starlinkLocation, setStarlinkLocation] = useState(null);
  // Bumped after a successful priority change to reload the report.
  const [reloadKey, setReloadKey] = useState(0);
  
  // Alert states
  const [alerts, setAlerts] = useState([]);
  const [vlanAlerts, setVlanAlerts] = useState(null);
  const [wanCapAlerts, setWanCapAlerts] = useState(null);
  const [starlinkAlerts, setStarlinkAlerts] = useState(null);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);

  const isClient = userType === "client";

  // Starlink overage is internal-only: never fetched or shown for client users,
  // and skipped for any user whose account has skip_starlink set.
  const shouldSkipStarlink = isClient || !!user?.skip_starlink;

  const getAlertDateParams = () => {
    if (period === "daily") {
      return `&date=${selectedDate}`;
    }

    // Prefer the exact window the report aggregated over.
    const dates = deviceData?.availableDates;
    if (dates && dates.length > 0) {
      const end = dates[0];                 // newest first
      const start = dates[dates.length - 1]; // oldest
      return `&start_date=${start}&end_date=${end}`;
    }

    // Fallback: compute the window from selectedDate if the report hasn't loaded yet.
    // const numDays = period === "monthly" ? 30 : 7;
    // const end = new Date(selectedDate);
    // const start = new Date(end);
    // start.setDate(start.getDate() - (numDays - 1));
    // const fmt = (d) => d.toISOString().split("T")[0];
    // return `&start_date=${fmt(start)}&end_date=${fmt(end)}`;

    const numDays = period === "monthly" ? 30 : 7;
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    const end = new Date(`${selectedDate}T00:00:00`); // parse as local
    const start = new Date(end);
    start.setDate(start.getDate() - (numDays - 1));
    return `&start_date=${fmt(start)}&end_date=${fmt(end)}`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return 'Invalid date';
    
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day} ${month} ${year} ${hours}:${minutes}`;
  };

  // Responsive listener
  useEffect(() => {
    const handleResize = () =>
      setIsMobile(window.innerWidth <= 768);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Parse device ID to get organization_id
  const parseDeviceId = (deviceIdString) => {
    if (!deviceIdString) return null;
    
    const parts = deviceIdString.split(':');
    const deviceInfo = {};
    
    for (let i = 0; i < parts.length; i += 2) {
      const key = parts[i];
      const value = parts[i + 1];
      if (key && value) {
        deviceInfo[key] = value;
      }
    }
    
    return deviceInfo;
  };

  // Fetch Device ID
  useEffect(() => {
    if (!serialNumber) return;

    const fetchDeviceId = async () => {
      try {
        const response = await apiFetch(
          `/api/monitoring/device-id?serial_number=${serialNumber}`
        );
        const data = await response.json();
        setSelectedDeviceId(data.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchDeviceId();
  }, [serialNumber]);

  // Fetch management type (for WAN priority change)
  useEffect(() => {
    if (!serialNumber) return;
    let cancelled = false;
    setManagementType(null);
    setIc2Writable(false);
    (async () => {
      try {
        const res = await apiFetch(
          `/api/monitoring/management-type?serial_number=${encodeURIComponent(serialNumber)}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setManagementType(data.management_type || null);
          setIc2Writable(data.can_change_priority === true);
        }
      } catch (err) {
        console.error(err); // stays null -> priority change disabled
      }
    })();
    return () => { cancelled = true; };
  }, [serialNumber]);

  // Fetch Device Data
  useEffect(() => {
    if (!selectedDeviceId) return;

    const fetchData = async () => {
      setIsLoading(true);
      setIsLoadingAlerts(true);
      try {
        // Endpoint depends on the selected period. Weekly/monthly treat `date`
        // as the END of the window (defaults to the selected date / today).
        const endpointByPeriod = {
          daily: "/api/monitoring/all-data",
          weekly: "/api/monitoring/weekly-data",
          monthly: "/api/monitoring/monthly-data",
        };
        const endpoint = endpointByPeriod[period] || endpointByPeriod.daily;

        const response = await apiFetch(
          `${endpoint}?device=${selectedDeviceId}&date=${selectedDate}`
        );
        const data = await response.json();
        setDeviceData(data);
        setLastUpdated(formatDateTime(new Date()));
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedDeviceId, selectedDate, period, reloadKey]);

  // Starlink live-location fallback. Cheap (backend reads its own ELCOME
  // cache, no live satellite call), so it's fetched once per device and
  // the layouts decide: Peplink GPS history always wins when the report
  // has usable points; this is only drawn when it doesn't.
  useEffect(() => {
    setStarlinkLocation(null);
    if (!serialNumber) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(
          `/api/monitoring/starlink-location?serial_number=${encodeURIComponent(serialNumber)}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStarlinkLocation(data.location || null);
      } catch (err) {
        console.error(err); // no fallback -> map hidden when no Peplink GPS
      }
    })();
    return () => { cancelled = true; };
  }, [serialNumber]);

  // Fetch Alerts based on organization_id and selected date
  useEffect(() => {
    if (!serialNumber || !selectedDeviceId) return;
    // Wait for the report window for weekly/monthly so the alert range matches.
    if (period !== "daily" && !deviceData?.availableDates) return;

    const fetchAlerts = async () => {
      setIsLoadingAlerts(true);
      const dateParams = getAlertDateParams();

      try {
        const importanceResponse = await apiFetch(
          `/api/importance/alerts/device/${serialNumber}?limit=100${dateParams}`
        );
        if (importanceResponse.ok) {
          const importanceData = await importanceResponse.json();
          setAlerts(importanceData.alerts || []);
        }

        const vlanResponse = await apiFetch(
          `/api/vlan/alerts/device/${serialNumber}?limit=100${dateParams}`
        );
        if (vlanResponse.ok) {
          const vlanAlertData = await vlanResponse.json();
          setVlanAlerts(vlanAlertData.alerts || []);
        }

        // WAN usage-cap alerts (independent of VLAN caps; combined in the UI as "Cap")
        const wanCapResponse = await apiFetch(
          `/api/wan/alerts/device/${serialNumber}?limit=100${dateParams}`
        );
        if (wanCapResponse.ok) {
          const wanCapAlertData = await wanCapResponse.json();
          setWanCapAlerts(wanCapAlertData.alerts || []);
        }

        if (!shouldSkipStarlink) {
          const starlinkResponse = await apiFetch(
            `/api/starlink/alerts/device/${serialNumber}?limit=50${dateParams}`
          );
          if (starlinkResponse.ok) {
            const starlinkData = await starlinkResponse.json();
            setStarlinkAlerts(starlinkData.alerts || []);
          }
        } else {
          setStarlinkAlerts(null);
        }
      } catch (err) {
        console.error("Error fetching alerts:", err);
        setAlerts([]);
        setVlanAlerts([]);
        setWanCapAlerts([]);
        setStarlinkAlerts(null);
      } finally {
        setIsLoadingAlerts(false);
      }
    };

    fetchAlerts();
  }, [serialNumber, selectedDeviceId, selectedDate, period, deviceData?.availableDates]);

  const isReportReady = !!deviceData && !isLoading && !isLoadingAlerts;

  // Guided tour ("?" in FleetHeader). Same data for both layouts; the tour
  // picks desktop/mobile steps from isMobile and admin/client from scope.
  usePageTour("monitoring", {
    isMobile,
    serialNumber,
    deviceData,
    period,
    selectedDate,
    alerts,
    vlanAlerts,
    wanCapAlerts,
    starlinkAlerts,
    shouldSkipStarlink,
    managementType,
    starlinkLocation,
  }, { ready: !isLoading && !!deviceData });
  const sharedProps = {
    user,
    userType,
    isClient,
    serialNumber,
    selectedDate,
    setSelectedDate,
    period,
    setPeriod,
    deviceData,
    isLoading,
    lastUpdated,
    selectedDeviceId,
    alerts,
    vlanAlerts,
    wanCapAlerts,
    starlinkAlerts,
    isLoadingAlerts,
    isReportReady,
    shouldSkipStarlink,
    onLogout,
    managementType,
    ic2Writable,
    starlinkLocation,
    onPriorityChanged: () => setReloadKey((k) => k + 1),
  };

  return isMobile ? (
    <MobileLayout {...sharedProps} />
  ) : (
    <DesktopLayout {...sharedProps} />
  );
}

export default MainPage;
